import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Products from './pages/Products';
import Invoices from './pages/Invoices';
import Purchases from './pages/Purchases';
import Suppliers from './pages/Suppliers';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import ClosingStock from './pages/ClosingStock';
import License from './pages/License';
import Login from './pages/Login';
import './styles/App.css';

function AppContent() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [apiInitialized, setApiInitialized] = useState(true); // Always true now
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('authToken') === '1');
  const navigate = useNavigate();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    }
  }, []);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Check license status every minute when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkLicense = async () => {
      try {
        const response = await fetch('/api/license/status');
        const data = await response.json();
        
        // Logout if license is invalid or trial has expired
        if (!data.isValid || (data.isTrial && data.daysRemaining <= 0)) {
          console.log('License expired or trial ended. Logging out...');
          handleLogout();
        }
      } catch (error) {
        console.error('Error checking license:', error);
      }
    };

    // Check license status immediately and then every minute
    checkLicense();
    const interval = setInterval(checkLicense, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleNewInvoice = () => {
    navigate('/invoices');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openInvoiceTypeModal'));
    }, 100);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const handleLoginSuccess = () => {
    localStorage.setItem('authToken', '1');
    setIsAuthenticated(true);
    navigate('/');
  };

  if (!apiInitialized) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Initializing...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="app-container login-container">
        <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLoginSuccess} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </main>
      </div>
    );
  }

  return (
    <div className={`app-container ${isCollapsed ? 'sidebar-collapsed' : 'sidebar-open'}`}>
      <Sidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        onNewInvoice={handleNewInvoice}
        isDarkMode={isDarkMode}
        onToggleDarkMode={() => setIsDarkMode((prev) => !prev)}
        onLogout={handleLogout}
      />
      <main className={`main-content ${isCollapsed ? 'expanded' : ''}`}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/license" element={<License />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/products" element={<Navigate to="/closing-stock" replace />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/purchases" element={<Purchases />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/closing-stock" element={<ClosingStock />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;

